import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import PaywallModal from '../components/PaywallModal'
import ImageCropperModal from '../components/ImageCropperModal'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { storage, db } from '../utils/firebase'
import { collection, query, where, getDocs, getDoc, addDoc, deleteDoc, doc, updateDoc, Timestamp } from 'firebase/firestore'
import { formatPhoneNumber, formatPhoneTyping } from '../utils/phone'
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Briefcase,
  Heart,
  Baby,
  Sparkles,
  Calendar,
  Camera,
  Save,
  Loader,
  Eye,
  EyeOff,
  Bell,
  Shield,
  Check,
  Home,
  UserPlus,
  Users,
  Star,
  Clock,
  MoreVertical,
  X,
  Send,
  Trash2,
  Info
} from 'lucide-react'
import './ProfilePage.css'

function ProfilePage() {
  const { userProfile, updateProfile, updateEmail, sendVerificationEmail, resendEmailChangeVerification, cancelPendingEmailChange, currentUser, isSubscriptionActive, unhideNeighbor } = useAuth()
  const isAdmin = userProfile?.isAdmin || userProfile?.isBoardMember || userProfile?.isSuperAdmin
const [searchParams] = useSearchParams()
const tabFromUrl = searchParams.get('tab')
const [activeTab, setActiveTab] = useState(
    tabFromUrl === 'household' ? 'household' :
    tabFromUrl === 'hidden' ? 'hidden' :
    tabFromUrl === 'privacy' ? 'privacy' :
    tabFromUrl === 'settings' ? 'privacy' :
    'profile'
  )
  const [neighborhoodName, setNeighborhoodName] = useState('')
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState('')

  // Cropper state — when the user picks a file we read it as a data URL and
  // hand it to the cropper modal; the cropper returns a 400x400 JPEG Blob
  // we upload in place of the original.
  const [imageToCrop, setImageToCrop] = useState(null)
  
  // Form state. We track firstName/lastName as the source of truth and
  // recompose `name` on save so the schema stays compatible with legacy
  // consumers that read `name` (Android already writes all three; iOS
  // writes only `name`).
  const [formData, setFormData] = useState({
    firstName: userProfile?.firstName || '',
    lastName: userProfile?.lastName || '',
    phone: userProfile?.phone || '',
    occupation: userProfile?.occupation || '',
    // Optional bio fields — shown in the Directory hover card. Each is
    // free-text, no validation, and saving an empty string clears them.
    partner: userProfile?.partner || '',
    children: userProfile?.children || '',
    hobbies: userProfile?.hobbies || '',
    address: userProfile?.address || '',
    showPhone: userProfile?.showPhone ?? true,
    showEmail: userProfile?.showEmail ?? true,
    showInDirectory: userProfile?.showInDirectory ?? true
  })
  const [nameError, setNameError] = useState('')
  const [phoneError, setPhoneError] = useState('')

  // When the profile finishes loading (or refreshes after a save), copy the
  // canonical fields back into the editable form so we don't show stale
  // values. We skip this while the user is mid-edit so we don't clobber
  // unsaved changes.
  useEffect(() => {
    if (editing) return
    setFormData(prev => ({
      ...prev,
      firstName: userProfile?.firstName || '',
      lastName: userProfile?.lastName || '',
      phone: userProfile?.phone || '',
      occupation: userProfile?.occupation || '',
      partner: userProfile?.partner || '',
      children: userProfile?.children || '',
      hobbies: userProfile?.hobbies || '',
      address: userProfile?.address || '',
      showPhone: userProfile?.showPhone ?? true,
      showEmail: userProfile?.showEmail ?? true,
      showInDirectory: userProfile?.showInDirectory ?? true,
    }))
  }, [userProfile?.firstName, userProfile?.lastName, userProfile?.phone, userProfile?.occupation, userProfile?.partner, userProfile?.children, userProfile?.hobbies, userProfile?.address, userProfile?.showPhone, userProfile?.showEmail, userProfile?.showInDirectory, editing])

  // Email change state
  const [showEmailChange, setShowEmailChange] = useState(false)
  const [newEmail, setNewEmail] = useState("")
  const [emailChangeLoading, setEmailChangeLoading] = useState(false)
  const [emailChangeMessage, setEmailChangeMessage] = useState("")
  const [currentPassword, setCurrentPassword] = useState("")

  // Email verification state
  const [verificationLoading, setVerificationLoading] = useState(false)
  const [verificationMessage, setVerificationMessage] = useState("")
  const [cancelVerifyLoading, setCancelVerifyLoading] = useState(false)

  // Hidden neighbors state
  const [hiddenNeighborDetails, setHiddenNeighborDetails] = useState([])
  const [loadingHidden, setLoadingHidden] = useState(false)

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deletingAccount, setDeletingAccount] = useState(false)

  useEffect(() => {
    const loadNeighborhoodName = async () => {
      if (userProfile?.neighborhoodId) {
        try {
          const neighborhoodDoc = await getDoc(doc(db, "neighborhoods", userProfile.neighborhoodId))
          if (neighborhoodDoc.exists()) {
            setNeighborhoodName(neighborhoodDoc.data().name || userProfile.neighborhoodId)
          }
        } catch (e) {
          console.error("Error loading neighborhood:", e)
        }
      }
    }
    loadNeighborhoodName()
  }, [userProfile?.neighborhoodId])

  // Load hidden neighbor details when privacy tab is active
  useEffect(() => {
    const loadHiddenNeighbors = async () => {
      const hiddenIds = userProfile?.hiddenNeighbors || []
      if (hiddenIds.length === 0) {
        setHiddenNeighborDetails([])
        return
      }
      setLoadingHidden(true)
      try {
        const usersQuery = query(
          collection(db, 'users'),
          where('email', 'in', hiddenIds)
        )
        const snapshot = await getDocs(usersQuery)
        const details = snapshot.docs.map(d => ({
          id: d.id,
          email: d.data().email,
          name: d.data().name || d.data().firstName || 'Unknown',
          address: d.data().address || ''
        }))
        setHiddenNeighborDetails(details)
      } catch (e) {
        console.error('Error loading hidden neighbors:', e)
      } finally {
        setLoadingHidden(false)
      }
    }
    if (activeTab === 'hidden') {
      loadHiddenNeighbors()
    }
  }, [activeTab, userProfile?.hiddenNeighbors])

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    const trimmedFirst = (formData.firstName || '').trim()
    const trimmedLast = (formData.lastName || '').trim()
    if (!trimmedFirst || !trimmedLast) {
      setNameError('First name and last name are both required.')
      return
    }
    setNameError('')

    // Phone is optional, but if provided it must be exactly 10 digits.
    // The onChange already strips non-digits, so we just length-check here.
    const phoneDigits = (formData.phone || '').replace(/\D/g, '')
    if (phoneDigits.length > 0 && phoneDigits.length !== 10) {
      setPhoneError('Phone number must be exactly 10 digits.')
      return
    }
    setPhoneError('')
    try {
      setSaving(true)
      // Persist firstName/lastName explicitly and recompose `name` so
      // legacy readers (any code still reading `name` directly) keep
      // working without a Firestore migration.
      const composedName = `${trimmedFirst} ${trimmedLast}`
      await updateProfile({
        ...formData,
        firstName: trimmedFirst,
        lastName: trimmedLast,
        name: composedName,
        // Persist the digit-only form so the formatter renders consistently
        // and we don't carry hyphens/spaces from legacy data.
        phone: phoneDigits || null,
        // Trim and null-coerce the bio fields so cleared values save as
        // null (matching how the load path treats them) rather than as
        // empty strings, which would still trigger the "show bio icon"
        // check in the Directory.
        partner: (formData.partner || '').trim() || null,
        children: (formData.children || '').trim() || null,
        hobbies: (formData.hobbies || '').trim() || null,
      })
      setEditing(false)
      setSuccess('Profile updated successfully!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      console.error('Error saving profile:', error)
    } finally {
      setSaving(false)
    }
  }

  const handlePhotoUpload = (e) => {
    // Don't upload yet — load the picked file into the cropper modal so the
    // user can pan/zoom to a circular crop. We upload the cropped result via
    // handleCroppedPhoto below.
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      setImageToCrop(reader.result)
    }
    reader.readAsDataURL(file)
    // Reset the input so picking the same file twice still triggers onChange.
    e.target.value = ''
  }

  const handleCroppedPhoto = async (blob) => {
    setImageToCrop(null)
    if (!blob) return
    try {
      setSaving(true)
      const storageRef = ref(storage, `profile_photos/${userProfile.uid}_${Date.now()}.jpg`)
      await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' })
      const downloadURL = await getDownloadURL(storageRef)
      await updateProfile({ profilePhotoURL: downloadURL })
      setSuccess('Photo updated!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      console.error('Error uploading photo:', error)
    } finally {
      setSaving(false)
    }
  }

  const formatJoinDate = (date) => {
    if (!date) return ''
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getRole = () => {
    if (userProfile?.isSuperAdmin) return { label: 'Super Admin', color: 'purple' }
    if (userProfile?.isBoardMember) {
      const title = (userProfile?.boardTitle || '').trim()
      return {
        label: title ? `Board Member · ${title}` : 'Board Member',
        color: 'purple',
      }
    }
    if (userProfile?.isAdmin) {
      const title = (userProfile?.adminTitle || '').trim()
      return {
        label: title ? `Neighborhood Admin · ${title}` : 'Neighborhood Admin',
        color: 'green',
      }
    }
    return null
  }

  const role = getRole()

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return
    setDeletingAccount(true)
    try {
      const { httpsCallable } = await import('firebase/functions')
      const { functions } = await import('../utils/firebase')
      const deleteUserAccount = httpsCallable(functions, 'deleteUserAccount')
      await deleteUserAccount()
      // Account deleted — sign out and redirect
      const { signOut } = await import('firebase/auth')
      const { auth } = await import('../utils/firebase')
      await signOut(auth)
      window.location.href = '/'
    } catch (error) {
      console.error('Error deleting account:', error)
      setSuccess('')
      alert('Failed to delete account: ' + error.message)
      setDeletingAccount(false)
    }
  }

  return (
    <div className="profile-page">
      {/* Crop modal — rendered at the top so it overlays everything when the
          user picks a photo. handleCroppedPhoto runs the actual upload. */}
      {imageToCrop && (
        <ImageCropperModal
          imageSrc={imageToCrop}
          onCancel={() => setImageToCrop(null)}
          onCropDone={handleCroppedPhoto}
        />
      )}

      {/* Profile Header */}
      <div className="profile-header" data-walk="profile-header">
        <div className="profile-photo-container">
          <div className="profile-photo">
            {userProfile?.profilePhotoURL ? (
              <img src={userProfile.profilePhotoURL} alt={userProfile.name} />
            ) : (
              <span>{userProfile?.name?.charAt(0).toUpperCase() || 'U'}</span>
            )}
          </div>
          <label className="photo-upload-btn">
            <Camera size={18} />
            <input 
              type="file" 
              accept="image/*" 
              onChange={handlePhotoUpload}
              hidden
            />
          </label>
        </div>
        
        <div className="profile-info">
          <h1>{userProfile?.name}</h1>
          <p className="neighborhood">{neighborhoodName || userProfile?.neighborhoodId}</p>
          {role && (
            <span className={`role-badge ${role.color}`}>
              {role.label}
            </span>
          )}
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="success-message">
          <Check size={18} />
          {success}
        </div>
      )}

      {/* Tabs */}
      <div className="profile-tabs">
        <button 
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <User size={18} />
          Profile
        </button>
        <button
          className={`tab ${activeTab === 'household' ? 'active' : ''}`}
          onClick={() => setActiveTab('household')}
        >
          <Home size={18} />
          My Household
        </button>
        <button
          className={`tab ${activeTab === 'hidden' ? 'active' : ''}`}
          onClick={() => setActiveTab('hidden')}
        >
          <EyeOff size={18} />
          Hidden Neighbors
        </button>
        <button
          className={`tab ${activeTab === 'privacy' ? 'active' : ''}`}
          onClick={() => setActiveTab('privacy')}
        >
          <Eye size={18} />
          Privacy
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'profile' && (
          <div className="profile-section">
            <div className="section-header">
              <h2>Profile Information</h2>
              {!editing ? (
                <button className="edit-btn" onClick={() => setEditing(true)}>
                  Edit Profile
                </button>
              ) : (
                <div className="edit-actions">
                  <button className="cancel-btn" onClick={() => {
                    setEditing(false)
                    setNameError('')
                    setFormData({
                      firstName: userProfile?.firstName || '',
                      lastName: userProfile?.lastName || '',
                      phone: userProfile?.phone || '',
                      occupation: userProfile?.occupation || '',
                      partner: userProfile?.partner || '',
                      children: userProfile?.children || '',
                      hobbies: userProfile?.hobbies || '',
                      address: userProfile?.address || '',
                      showPhone: userProfile?.showPhone ?? true,
                      showEmail: userProfile?.showEmail ?? true,
                      showInDirectory: userProfile?.showInDirectory ?? true
                    })
                  }}>
                    Cancel
                  </button>
                  <button className="save-btn" onClick={handleSave} disabled={saving}>
                    {saving ? <Loader size={18} className="spinner" /> : <Save size={18} />}
                    Save
                  </button>
                </div>
              )}
            </div>

            <div className="info-grid">
              {/* Name — split into First / Last when editing; both required.
                  In view mode we show the joined `name` for parity with
                  what neighbors see in the directory. */}
              {editing ? (
                <>
                  <div className="info-item">
                    <label>
                      <User size={16} />
                      First Name <span className="required-mark">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      required
                      autoComplete="given-name"
                    />
                  </div>
                  <div className="info-item">
                    <label>
                      <User size={16} />
                      Last Name <span className="required-mark">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      required
                      autoComplete="family-name"
                    />
                  </div>
                  {nameError && (
                    <div className="info-item info-item-full">
                      <p className="error-message">{nameError}</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="info-item">
                  <label>
                    <User size={16} />
                    Full Name
                  </label>
                  <span>{userProfile?.name}</span>
                </div>
              )}

              {/* Email */}
              <div className="info-item email-item">
                <label>
                  <Mail size={16} />
                  Email
                </label>
                {editing ? (
                  <div className="email-edit-section">
                    <input
                      type="email"
                      value={newEmail || userProfile?.email}
                      onChange={(e) => {
                        setNewEmail(e.target.value)
                        setEmailChangeMessage("")
                      }}
                      placeholder="Enter email address"
                    />
                    {newEmail && newEmail !== userProfile?.email && (
                      <>
                        {!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(newEmail) ? (
                          <p className="error-message">Please enter a valid email address.</p>
                        ) : (
                          <>
                            <input
                              type="password"
                              value={currentPassword}
                              onChange={(e) => {
                                setCurrentPassword(e.target.value)
                                setEmailChangeMessage("")
                              }}
                              placeholder="Enter password to confirm email change"
                              className="password-confirm"
                            />
                            <button
                              type="button"
                              className="send-verification-btn"
                              disabled={emailChangeLoading || !currentPassword}
                              onClick={async () => {
                                setEmailChangeLoading(true)
                                setEmailChangeMessage("")
                                try {
                                  await updateEmail(newEmail, currentPassword)
                                  setEmailChangeMessage("Verification email sent! Check your inbox.")
                                  setNewEmail("")
                                  setCurrentPassword("")
                                } catch (err) {
                                  setEmailChangeMessage(err.message)
                                } finally {
                                  setEmailChangeLoading(false)
                                }
                              }}
                            >
                              {emailChangeLoading ? <Loader size={14} className="spinning" /> : "Send Verification"}
                            </button>
                          </>
                        )}
                      </>
                    )}
                    {emailChangeMessage && (
                      <p className={emailChangeMessage.includes("sent") ? "success-message" : "error-message"}>
                        {emailChangeMessage}
                      </p>
                    )}
                  </div>
                ) : (
                  <span>{userProfile?.email}</span>
                )}
                {userProfile?.pendingEmail && !editing && (
                  <div className="pending-email-notice">
                    <Info size={14} />
                    <span>Verification sent to <strong>{userProfile.pendingEmail}</strong>. Check your inbox.</span>
                    <div className="pending-email-actions">
                      <button
                        className="verify-email-link resend-btn"
                        onClick={async () => {
                          setVerificationLoading(true)
                          try {
                            // Resend the styled email-change verification to
                            // the PENDING address (not the current/old one) —
                            // sendVerificationEmail() targets the signed-in
                            // user's email, which would re-mail the old inbox.
                            await resendEmailChangeVerification(userProfile.pendingEmail)
                            setVerificationMessage(`Verification email sent to ${userProfile.pendingEmail}. Check your inbox.`)
                          } catch (err) {
                            setVerificationMessage(err.message)
                          } finally {
                            setVerificationLoading(false)
                          }
                        }}
                        disabled={verificationLoading || cancelVerifyLoading}
                      >
                        {verificationLoading ? "Sending..." : "Resend Email"}
                      </button>
                      <button
                        className="verify-email-link cancel-verify-btn"
                        onClick={async () => {
                          setCancelVerifyLoading(true)
                          setVerificationMessage("")
                          try {
                            // Marks the in-flight oobCode cancelled in
                            // Firestore (so /auth/action will refuse it)
                            // and clears pendingEmail on the user doc, so
                            // this whole banner unmounts on the next render.
                            await cancelPendingEmailChange()
                          } catch (err) {
                            setVerificationMessage(err.message)
                          } finally {
                            setCancelVerifyLoading(false)
                          }
                        }}
                        disabled={cancelVerifyLoading || verificationLoading}
                      >
                        {cancelVerifyLoading ? "Cancelling..." : "Cancel Verification"}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Phone — input strips non-digits and caps at 10 so any
                  paste/autocomplete/keyboard input converges on a clean
                  digit-only string. We store digits in formData but render
                  a partially-formatted view ("(402) 555-1234") so the user
                  sees the shape they expect while typing. handleSave
                  enforces exactly 10 digits when a phone is provided. */}
              <div className="info-item">
                <label>
                  <Phone size={16} />
                  Phone
                </label>
                {editing ? (
                  <>
                    <input
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      maxLength={14}
                      value={formatPhoneTyping(formData.phone || '')}
                      onChange={(e) => {
                        const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10)
                        handleChange('phone', digitsOnly)
                        if (phoneError) setPhoneError('')
                      }}
                      placeholder="(555) 555-5555"
                    />
                    {phoneError && <p className="error-message">{phoneError}</p>}
                  </>
                ) : (
                  <span>{formatPhoneNumber(userProfile?.phone) || 'Not provided'}</span>
                )}
              </div>

              {/* Address */}
              <div className="info-item">
                <label>
                  <MapPin size={16} />
                  Address
                </label>
                {editing && isAdmin ? (
                  <input
                    type="text"
                    value={formData.address}
                    onChange={e => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Your address"
                  />
                ) : (
                  <span>{userProfile?.address}</span>
                )}
              </div>

              {/* Occupation */}
              <div className="info-item">
                <label>
                  <Briefcase size={16} />
                  Occupation
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.occupation}
                    onChange={(e) => handleChange('occupation', e.target.value)}
                    placeholder="Your occupation"
                  />
                ) : (
                  <span>{userProfile?.occupation || 'Not provided'}</span>
                )}
              </div>

              {/* Partner — optional. Shown in the Directory hover card
                  when populated. */}
              <div className="info-item">
                <label>
                  <Heart size={16} />
                  Partner
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.partner}
                    onChange={(e) => handleChange('partner', e.target.value)}
                    placeholder="Spouse or partner's name (optional)"
                  />
                ) : (
                  <span>{userProfile?.partner || 'Not provided'}</span>
                )}
              </div>

              {/* Children — optional. Free-text so users can write
                  whatever feels natural ("Two kids, 8 and 11" or
                  "Sam, Riley, Avery"). */}
              <div className="info-item">
                <label>
                  <Baby size={16} />
                  Children
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.children}
                    onChange={(e) => handleChange('children', e.target.value)}
                    placeholder="Names and/or ages (optional)"
                  />
                ) : (
                  <span>{userProfile?.children || 'Not provided'}</span>
                )}
              </div>

              {/* Hobbies — optional. */}
              <div className="info-item">
                <label>
                  <Sparkles size={16} />
                  Hobbies
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.hobbies}
                    onChange={(e) => handleChange('hobbies', e.target.value)}
                    placeholder="Reading, gardening, pickleball... (optional)"
                  />
                ) : (
                  <span>{userProfile?.hobbies || 'Not provided'}</span>
                )}
              </div>

              {/* Member Since */}
              <div className="info-item">
                <label>
                  <Calendar size={16} />
                  Member Since
                </label>
                <span>{formatJoinDate(userProfile?.joinedDate)}</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'household' && (
          <div className="household-tab-wrapper">
            {!isSubscriptionActive() && <PaywallModal isAdmin={isAdmin} />}
          <HouseholdSection userProfile={userProfile} setSuccess={setSuccess} />
          </div>
        )}

        {activeTab === 'privacy' && (
          <div className="privacy-section">
            <h2>Privacy Settings</h2>
            <p className="section-description">
              Control what information is visible to other members in the directory.
            </p>

            <div className="privacy-options">
              <div className="privacy-option">
                <div className="option-info">
                  <h3>Show Profile</h3>
                  <p>Display your profile in the directory</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={formData.showInDirectory}
                    onChange={(e) => {
                      handleChange('showInDirectory', e.target.checked)
                      updateProfile({ showInDirectory: e.target.checked })
                    }}
                  />
                  <span className="slider"></span>
                </label>
              </div>

              <div className="privacy-option">
                <div className="option-info">
                  <h3>Show Email</h3>
                  <p>Display your email address in the directory</p>
                  {!currentUser?.emailVerified && (
                    <div className="verify-email-prompt">
                      {!verificationMessage.includes("sent") && (
                        <button 
                          className="verify-email-link"
                          onClick={async () => {
                            setVerificationLoading(true)
                            setVerificationMessage("")
                            try {
                              await sendVerificationEmail()
                              setVerificationMessage("Verification email sent! Check your inbox.")
                            } catch (err) {
                              setVerificationMessage(err.message)
                            } finally {
                              setVerificationLoading(false)
                            }
                          }}
                          disabled={verificationLoading}
                        >
                          {verificationLoading ? "Sending..." : "Verify Email"}
                        </button>
                      )}
                      {verificationMessage && (
                        <>
                          <span className={verificationMessage.includes("sent") ? "success-text" : "error-text"}>
                            {verificationMessage}
                          </span>
                          {verificationMessage.includes("sent") && (
                            <button
                              className="verify-email-link resend-btn"
                              onClick={async () => {
                                setVerificationLoading(true)
                                setVerificationMessage("")
                                try {
                                  await sendVerificationEmail()
                                  setVerificationMessage("Verification email sent! Check your inbox.")
                                } catch (err) {
                                  setVerificationMessage(err.message)
                                } finally {
                                  setVerificationLoading(false)
                                }
                              }}
                              disabled={verificationLoading}
                            >
                              {verificationLoading ? "Sending..." : "Resend Email"}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={currentUser?.emailVerified ? formData.showEmail : false}
                    disabled={!currentUser?.emailVerified}
                    onChange={(e) => {
                      handleChange('showEmail', e.target.checked)
                      updateProfile({ showEmail: e.target.checked })
                    }}
                  />
                  <span className={`slider ${!currentUser?.emailVerified ? 'disabled' : ''}`}></span>
                </label>
              </div>

              <div className="privacy-option">
                <div className="option-info">
                  <h3>Show Phone Number</h3>
                  <p>Display your phone number in the directory</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={formData.showPhone}
                    onChange={(e) => {
                      handleChange('showPhone', e.target.checked)
                      updateProfile({ showPhone: e.target.checked })
                    }}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>

          {/* Delete Account Section */}
          <div className="delete-account-section">
            <h2><Trash2 size={20} /> Delete Account</h2>
            <p>Permanently delete your NeighborTopia account and all associated data. This action cannot be undone.</p>
            {isAdmin && (
              <div className="admin-delete-warning">
                <Shield size={16} />
                <span>You are an admin/board member. You must transfer your admin role to another member before you can delete your account.</span>
              </div>
            )}
            {!showDeleteConfirm ? (
              <button className="btn-delete-account" onClick={() => setShowDeleteConfirm(true)} disabled={isAdmin}>
                <Trash2 size={16} /> Delete My Account
              </button>
            ) : (
              <div className="delete-confirm-box">
                <p className="delete-warning">
                  This will permanently delete your profile, posts, comments, messages, and all other data. You will be signed out immediately.
                </p>
                <p className="delete-instruction">Type <strong>DELETE</strong> to confirm:</p>
                <input
                  type="text"
                  className="delete-confirm-input"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE to confirm"
                  disabled={deletingAccount}
                />
                <div className="delete-confirm-buttons">
                  <button
                    className="btn-cancel-delete"
                    onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText('') }}
                    disabled={deletingAccount}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-confirm-delete"
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
                  >
                    {deletingAccount ? (
                      <><Loader size={16} className="spinner" /> Deleting...</>
                    ) : (
                      <><Trash2 size={16} /> Permanently Delete Account</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
          </div>
        )}

        {activeTab === 'hidden' && (
          <div className="privacy-section">
            <h2>Hidden Neighbors</h2>
            <p className="section-description">
              Hidden neighbors won't appear in discussions or comments, but will still be visible in the Directory.
            </p>

            {loadingHidden ? (
              <div className="hidden-loading">
                <Loader size={18} className="spinning" />
                <span>Loading...</span>
              </div>
            ) : hiddenNeighborDetails.length === 0 ? (
              <div className="hidden-empty">
                <EyeOff size={24} />
                <p>No hidden neighbors</p>
                <span>Neighbors you hide from discussions will appear here.</span>
              </div>
            ) : (
              <div className="hidden-list">
                {hiddenNeighborDetails.map(neighbor => (
                  <div key={neighbor.email} className="hidden-neighbor-row">
                    <div className="hidden-neighbor-info">
                      <div className="hidden-neighbor-avatar">
                        {neighbor.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div className="hidden-neighbor-details">
                        <span className="hidden-neighbor-name">{neighbor.name}</span>
                        {neighbor.address && <span className="hidden-neighbor-address">{neighbor.address}</span>}
                      </div>
                    </div>
                    <button
                      className="btn-unhide"
                      onClick={() => unhideNeighbor(neighbor.email)}
                    >
                      <Eye size={14} />
                      Unhide
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ============ Household Section ============

function HouseholdSection({ userProfile, setSuccess }) {
  const [householdMembers, setHouseholdMembers] = useState([])
  const [pendingInvites, setPendingInvites] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [neighborhoodName, setNeighborhoodName] = useState("")
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteMenuOpen, setInviteMenuOpen] = useState(null)


  useEffect(() => {
    if (userProfile?.address) {
      loadHouseholdData()
    }
  }, [userProfile?.address])

  const loadHouseholdData = async () => {
    try {
      setIsLoading(true)
      
      // Load household members (users with same address)
      const usersQuery = query(
        collection(db, 'users'),
        where('address', '==', userProfile.address)
      )
      const usersSnap = await getDocs(usersQuery)
      const members = usersSnap.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          name: data.name || '',
          email: data.email || '',
          joinedDate: data.joinedDate?.toDate() || new Date(),
          isPrimary: data.isPrimaryResident || false,
          profilePhotoURL: data.profilePhotoURL
        }
      }).sort((a, b) => a.joinedDate - b.joinedDate)

      // Determine primary (earliest joined if none marked)
      const hasPrimary = members.some(m => m.isPrimary)
      const membersWithRoles = members.map((member, index) => ({
        ...member,
        isPrimary: hasPrimary ? member.isPrimary : index === 0,
        role: (hasPrimary ? member.isPrimary : index === 0) ? 'Primary Resident' : 'Secondary Resident'
      }))

      setHouseholdMembers(membersWithRoles)

      // Load pending invites
      const invitesQuery = query(
        collection(db, 'householdInvites'),
        where('address', '==', userProfile.address),
        where('status', '==', 'pending')
      )
      const invitesSnap = await getDocs(invitesQuery)
      const invites = invitesSnap.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          name: data.inviteeName || '',
          email: data.inviteeEmail || '',
          sentDate: data.createdDate?.toDate() || new Date(),
          code: data.code || ''
        }
      })

      setPendingInvites(invites)
    } catch (error) {
      console.error('Error loading household data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteInvite = async (inviteId) => {
    try {
      await deleteDoc(doc(db, 'householdInvites', inviteId))
      setPendingInvites(prev => prev.filter(i => i.id !== inviteId))
      setSuccess('Invitation deleted')
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      console.error('Error deleting invite:', error)
    }
    setInviteMenuOpen(null)
  }

const handleResendInvite = async (invite) => {
    try {
      // Update the invite document to trigger resend via Cloud Function
      const inviteRef = doc(db, 'householdInvites', invite.id)
      await updateDoc(inviteRef, {
        resendRequested: true,
        resendRequestedAt: Timestamp.now(),
        resendCount: (invite.resendCount || 0) + 1
      })
      setSuccess('Invitation email resent!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      console.error('Error resending invite:', error)
      alert('Failed to resend invitation')
    }
    setInviteMenuOpen(null)
  }

  const totalCount = householdMembers.length + pendingInvites.length

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const formatRelativeDate = (date) => {
    const now = new Date()
    const diffMs = now - date
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor(diffMs / (1000 * 60))

    if (diffMinutes < 1) return 'just now'
    if (diffMinutes < 60) return `${diffMinutes}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return formatDate(date)
  }

  if (isLoading) {
    return (
      <div className="household-section">
        <div className="loading-state">
          <Loader size={24} className="spinner" />
          <p>Loading household members...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="household-section">
      <h2>My Household</h2>

      {/* Household Members */}
      {householdMembers.length > 0 && (
        <>
          <h3 className="subsection-title">
            <Users size={18} />
            Household Members ({householdMembers.length})
          </h3>
          <div className="household-members-list">
            {householdMembers.map(member => (
              <div key={member.id} className="household-member-card">
                <div className="member-avatar">
                  {member.profilePhotoURL ? (
                    <img src={member.profilePhotoURL} alt={member.name} />
                  ) : (
                    <span>{member.name.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="member-info">
                  <div className="member-name">
                    {member.name}
                    {member.isPrimary && <Star size={14} className="primary-icon" />}
                    {member.email === userProfile?.email && (
                      <span className="you-badge">You</span>
                    )}
                  </div>
                  <div className="member-role">{member.role}</div>
                  <div className="member-joined">Joined {formatDate(member.joinedDate)}</div>
                </div>
                <span className="member-status active">Active</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Pending Invitations */}
      {pendingInvites.length > 0 && (
        <>
          <h3 className="subsection-title">
            <Clock size={18} />
            Pending Invitations ({pendingInvites.length})
          </h3>
          <div className="pending-invites-list">
            {pendingInvites.map(invite => (
              <div key={invite.id} className="pending-invite-card">
                <div className="invite-icon">
                  <Clock size={24} />
                </div>
                <div className="invite-info">
                  <div className="invite-name">{invite.name || invite.email}</div>
                  <div className="invite-role">Invited as Secondary Resident</div>
                  <div className="invite-sent">Sent {formatRelativeDate(invite.sentDate)}</div>
                </div>
                <div className="invite-actions">
                  <button 
                    className="menu-btn"
                    onClick={() => setInviteMenuOpen(inviteMenuOpen === invite.id ? null : invite.id)}
                  >
                    <MoreVertical size={18} />
                  </button>
                  {inviteMenuOpen === invite.id && (
                    <div className="invite-menu">
                      <button onClick={() => handleResendInvite(invite)}>
                        <Send size={16} />
                        Resend Invite
                      </button>
                      <button className="delete" onClick={() => handleDeleteInvite(invite.id)}>
                        <Trash2 size={16} />
                        Delete Invite
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add Member Button */}
      {totalCount < 5 && (
        <button className="invite-member-btn" onClick={() => setShowInviteModal(true)}>
          <UserPlus size={20} />
          Invite Household Member
        </button>
      )}

      {/* Household Info */}
      <div className="household-info-card">
        <Info size={20} />
        <div>
          <h4>Household Limit</h4>
          <p>Your household can have up to 5 members total</p>
        </div>
        <span className="count">{totalCount}/5</span>
      </div>

      {/* Help Text */}
      <p className="help-text">
        Invite family members, roommates, or tenants to join your household. 
        They'll receive a secure invitation code via email.
      </p>

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteModal 
          userProfile={userProfile}
          onClose={() => setShowInviteModal(false)}
          onSuccess={(name) => {
            setShowInviteModal(false)
            setSuccess(`Invitation sent to ${name}!`)
            setTimeout(() => setSuccess(''), 3000)
            loadHouseholdData()
          }}
        />
      )}
    </div>
  )
}

// ============ Invite Modal ============

function InviteModal({ userProfile, onClose, onSuccess }) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')

  const isValidEmail = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email)
  const isFormValid = firstName.trim() && lastName.trim() && email.trim() && isValidEmail

  const generateInviteCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
    return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!isFormValid) return

    try {
      setSending(true)
      setError('')

      // Check if email is already registered
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', email.trim().toLowerCase())
      )
      const usersSnap = await getDocs(usersQuery)
      
      if (!usersSnap.empty) {
        setError('This email is already registered in NeighborTopia.')
        setSending(false)
        return
      }

      // Check if there's already a pending invite for this email
      const invitesQuery = query(
        collection(db, 'householdInvites'),
        where('inviteeEmail', '==', email.trim().toLowerCase()),
        where('status', '==', 'pending')
      )
      const invitesSnap = await getDocs(invitesQuery)
      
      if (!invitesSnap.empty) {
        setError('An invitation has already been sent to this email.')
        setSending(false)
        return
      }

      const inviteCode = generateInviteCode()
      const fullName = `${firstName.trim()} ${lastName.trim()}`

      await addDoc(collection(db, 'householdInvites'), {
        inviterEmail: userProfile.email,
        inviterName: userProfile.name,
        inviteeEmail: email.trim().toLowerCase(),
        inviteeName: fullName,
        address: userProfile.address,
        neighborhoodId: userProfile.neighborhoodId,
        code: inviteCode,
        status: 'pending',
        isUsed: false,
        role: 'Secondary Resident',
        createdDate: Timestamp.now(),
        resendCount: 0
      })

            // Email is sent automatically by Cloud Function


      onSuccess(fullName)
    } catch (err) {
      console.error('Error creating invite:', err)
      setError('Failed to create invitation. Please try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Invite Household Member</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>First Name</label>
            <input
              type="text"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              placeholder="Enter first name"
            />
          </div>

          <div className="form-group">
            <label>Last Name</label>
            <input
              type="text"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              placeholder="Enter last name"
            />
          </div>

          <div className="form-group">
            <label>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Enter email address"
              className={email && !isValidEmail ? 'error' : ''}
            />
            {email && !isValidEmail && (
              <span className="error-text">Please enter a valid email address</span>
            )}
          </div>

          <p className="modal-help-text">
            An email invitation with a secure code will be sent automatically.
          </p>

          {error && <p className="error-message">{error}</p>}

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={!isFormValid || sending}>
              {sending ? (
                <>
                  <Loader size={18} className="spinner" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send Invitation
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ProfilePage
